/**
 * Shop – /shop (NT-SHOP-01 + NT-MOLLIE-01 + NT-MOLLIE-CHECKOUT-01)
 *
 * Einstiegspunkt in den Shop. Während der Alpha ist ausschließlich "Früher Vogel"
 * aktiv buchbar – alle anderen Produkte werden als Preview mit "DEMNÄCHST"-Sperre
 * angezeigt (sichtbar, aber nicht klickbar/buchbar).
 *
 * Früher-Vogel-Checkout läuft über Mollie: Betragseingabe → Checkout-Formular
 * (E-Mail/Land/Widerruf, siehe openCheckoutModal) → POST /shop/checkout →
 * Redirect zur Mollie-Checkout-Seite.
 *
 * Design: Terminal-UI, Share Tech Mono, keine Emojis, keine Gradienten, kein box-shadow.
 * Farben ausschließlich via CSS Custom Properties.
 */

import { navigate } from '../../router.js';
import { SHOP_CATALOG } from '../../data/shop-catalog.js';
import { EU_COUNTRY_LABELS } from '../../data/eu-countries.js';
import { api, ApiError } from '../../services/api.js';
import { refreshBalance } from '../../utils/refreshBalance.js';

interface FrueherVogelStatus {
  hasBadge: boolean;
  paidAt: string | null;
  // KUMULATIVE Summe aller bisherigen Zahlungen (NT-SCHEINE-PAKETE-01, Aufgabe 5) —
  // auch ohne hasBadge bereits befüllt, sobald mindestens eine Zahlung erfolgreich war.
  amountCents: number | null;
}

/** Antwort von GET /shop/checkout/result?productKey=... (NT-SCHEINE-PAKETE-01, Aufgabe 4). */
interface ShopCheckoutResultResponse {
  status: 'paid' | 'pending' | 'failed' | 'none';
  amountCents: number | null;
  scheineGranted: number;
}

// Frontend-Sicherheitsgrenzen (Backend validiert unabhängig davon erneut serverseitig)
const FRUEHER_VOGEL_MIN_CENTS = 200; // 2,00 €
const FRUEHER_VOGEL_MAX_CENTS = 99_999; // 999,99 €
const FRUEHER_VOGEL_BADGE_THRESHOLD_CENTS = 5000; // 50,00 €

/** EUR-Betrag im deutschen Format (z.B. 4,20 €). Ganze Beträge ohne Nachkommastellen. */
function formatEUR(value: number): string {
  const hasFraction = !Number.isInteger(value);
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })} €`;
}

function addStyles(): void {
  const existing = document.getElementById('shop-styles');
  const el = existing ?? document.createElement('style');
  el.id = 'shop-styles';
  el.textContent = `
    .shop-page {
      max-width: 760px;
      margin: 0 auto;
      padding: 2rem 1rem 4rem;
      font-family: 'Share Tech Mono', 'Courier New', monospace;
      color: var(--text-primary);
    }
    .shop-back {
      background: none;
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      padding: 0.4rem 0.8rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      margin-bottom: 1.5rem;
      display: inline-block;
      min-height: 44px;
    }
    .shop-back:hover { color: var(--text-primary); border-color: var(--text-muted); }
    .shop-title {
      font-size: var(--font-size-xl);
      text-transform: uppercase;
      color: var(--text-primary);
      letter-spacing: 0.08em;
      margin-bottom: 0.4rem;
    }
    .shop-title-accent { color: var(--accent-gold); }
    .shop-intro {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      line-height: 1.5;
      margin-bottom: 2rem;
    }
    .shop-section {
      position: relative;
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }
    .shop-section--locked { opacity: 0.5; }
    .shop-section--active { border-color: var(--accent-gold); }
    .shop-section-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }
    .shop-section-title {
      font-size: var(--font-size-base);
      text-transform: uppercase;
      color: var(--text-primary);
      letter-spacing: 0.06em;
    }
    .shop-badge-soon {
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.06em;
      background: var(--text-muted);
      color: var(--bg-base);
      padding: 0.2rem 0.5rem;
      border-radius: var(--radius-sm);
      white-space: nowrap;
      flex-shrink: 0;
    }
    .shop-desc {
      font-size: var(--font-size-sm);
      color: var(--text-muted);
      line-height: 1.5;
      margin-bottom: 1rem;
    }
    .shop-btn {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 0.5rem 1.2rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      min-height: 44px;
    }
    .shop-btn--primary { border-color: var(--accent-gold); color: var(--accent-gold); }
    .shop-btn--primary:hover { background: var(--accent-gold); color: var(--bg-base); }
    .shop-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    /* Grid der Scheine-Pakete – Desktop mehrspaltig, Mobile Einzelspalte */
    .shop-package-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 0.75rem;
    }
    .shop-package-card {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 0.9rem;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .shop-package-name {
      font-size: var(--font-size-sm);
      text-transform: uppercase;
      color: var(--text-primary);
      letter-spacing: 0.04em;
    }
    .shop-package-scheine { font-size: var(--font-size-xs); color: var(--accent-gold); }
    .shop-package-bonus { color: var(--accent-green); }
    .shop-package-price { font-size: var(--font-size-xs); color: var(--text-muted); }
    .shop-package-card .shop-btn { margin-top: 0.4rem; text-align: center; }
    .shop-tier-row {
      font-size: var(--font-size-sm);
      color: var(--text-muted);
      letter-spacing: 0.04em;
      margin-bottom: 0.5rem;
    }
    @media (max-width: 560px) {
      .shop-package-grid { grid-template-columns: 1fr; }
    }
    .shop-fv-label {
      display: block;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--text-muted);
      letter-spacing: 0.05em;
      margin-bottom: 0.4rem;
    }
    .shop-fv-input {
      font-family: inherit;
      font-size: var(--font-size-sm);
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--accent-gold);
      border-radius: var(--radius-btn);
      padding: 0.5rem 0.75rem;
      width: 140px;
      margin-bottom: 0.5rem;
      -moz-appearance: textfield;
    }
    .shop-fv-input::-webkit-outer-spin-button,
    .shop-fv-input::-webkit-inner-spin-button { -webkit-appearance: none; }
    .shop-fv-input--invalid { border-color: var(--accent-red); }
    .shop-fv-hint {
      font-size: var(--font-size-xs);
      color: var(--accent-gold);
      margin-bottom: 0.75rem;
      min-height: 1em;
    }
    .shop-fv-error { color: var(--accent-red); }
    .shop-fv-progress {
      font-size: var(--font-size-xs);
      color: var(--accent-gold);
      margin-bottom: 0.75rem;
    }
    .shop-fv-disclaimer {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      line-height: 1.5;
      margin-top: 0.75rem;
    }
    .shop-fv-badge {
      display: inline-block;
      font-size: var(--font-size-sm);
      text-transform: uppercase;
      color: var(--accent-gold);
      border: 1px solid var(--accent-gold);
      border-radius: var(--radius-sm);
      padding: 0.2rem 0.6rem;
      letter-spacing: 0.06em;
      margin-bottom: 0.75rem;
    }
    .shop-fv-meta {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-top: 0.75rem;
    }
    .shop-flash {
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.6rem 1rem;
      border-radius: var(--radius);
    }
    .shop-flash--success { color: var(--accent-green); border: 1px solid var(--accent-green); }
    .shop-flash--muted { color: var(--text-muted); border: 1px solid var(--border); }
    .shop-flash--error { color: var(--accent-red); border: 1px solid var(--accent-red); }
    .shop-package-card .shop-flash { margin-top: 0.4rem; }
    .shop-checkout-summary {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 0.75rem 0.9rem;
      margin-bottom: 1rem;
    }
    .shop-checkout-summary-row {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 0.2rem 0;
    }
    .shop-checkout-summary-row span:first-child { color: var(--text-muted); }
    .shop-checkout-summary-row span:last-child { color: var(--text-primary); }
    .shop-checkout-summary-row--total span:last-child { color: var(--accent-gold); }
    /* Checkout-Modal (NT-MOLLIE-CHECKOUT-01) – Shell = .modal-backdrop/.modal aus
       dashboard.css (siehe components/BottomSheet.ts). */
    .shop-checkout-label {
      display: block;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--text-muted);
      letter-spacing: 0.05em;
      margin-bottom: 0.3rem;
    }
    .shop-checkout-input, .shop-checkout-select {
      width: 100%;
      font-family: inherit;
      font-size: var(--font-size-sm);
      background: var(--bg-surface);
      border: 1px solid var(--border);
      color: var(--text-primary);
      border-radius: var(--radius-btn);
      padding: 0.5rem 0.75rem;
      margin-bottom: 0.9rem;
    }
    .shop-checkout-input--invalid, .shop-checkout-select--invalid { border-color: var(--accent-red); }
    .shop-checkout-consent {
      display: flex;
      width: 100%;
      gap: 0.5rem;
      align-items: flex-start;
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      text-transform: none;
      letter-spacing: normal;
      line-height: 1.4;
      margin-bottom: 0.9rem;
    }
    .shop-checkout-consent input {
      flex: 0 0 auto;
      margin-top: 0.2rem;
      width: 16px;
      height: 16px;
    }
    .shop-checkout-consent span {
      flex: 1 1 0%;
      min-width: 0;
      word-break: normal;
      overflow-wrap: break-word;
    }
    .shop-checkout-privacy-hint {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      line-height: 1.5;
      margin-bottom: 1rem;
    }
    .shop-checkout-privacy-hint a { color: var(--accent-blue); }
    .shop-checkout-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }
    .shop-checkout-error {
      color: var(--accent-red);
      font-size: var(--font-size-xs);
      margin-bottom: 0.75rem;
      min-height: 1em;
    }
  `;
  if (!existing) document.head.appendChild(el);
}

function formatDateDE(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Validiert die Betragseingabe: positive Zahl, max. 2 Dezimalstellen, im erlaubten Bereich. */
function validateFrueherVogelAmount(raw: string): { valid: boolean; cents: number } {
  const trimmed = raw.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { valid: false, cents: 0 };
  const cents = Math.round(Number(trimmed) * 100);
  if (cents < FRUEHER_VOGEL_MIN_CENTS || cents > FRUEHER_VOGEL_MAX_CENTS) {
    return { valid: false, cents };
  }
  return { valid: true, cents };
}

/**
 * `alreadyPaidCents` — kumulative Summe bereits geleisteter Früher-Vogel-Zahlungen
 * (NT-SCHEINE-PAKETE-01, Aufgabe 5). 0, solange noch nie gezahlt wurde.
 */
function renderFrueherVogelForm(alreadyPaidCents: number): string {
  const p = SHOP_CATALOG.frueherVogel;
  const progressLine =
    alreadyPaidCents > 0
      ? `<p class="shop-fv-progress">Bisher unterstützt: ${formatEUR(alreadyPaidCents / 100)} von ${formatEUR(FRUEHER_VOGEL_BADGE_THRESHOLD_CENTS / 100)}</p>`
      : '';
  return `
    <section class="shop-section shop-section--active">
      <div class="shop-section-head">
        <span class="shop-section-title">${p.title} – ALPHA-UNTERSTÜTZUNG</span>
      </div>
      <p class="shop-desc">
        Unterstütze Nighttrade in der Alpha-Phase. Insgesamt ${formatEUR(FRUEHER_VOGEL_BADGE_THRESHOLD_CENTS / 100)}
        über beliebig viele Zahlungen hinweg bringen dir das FRÜHER VOGEL Badge.
      </p>
      ${progressLine}
      <label class="shop-fv-label" for="shop-fv-amount">Betrag (mind. 2 €)</label><br>
      <input type="number" id="shop-fv-amount" class="shop-fv-input" min="2" max="999.99" step="0.01" value="5.00">
      <div class="shop-fv-hint" id="shop-fv-hint"></div>
      <button class="shop-btn shop-btn--primary" id="shop-frueher-vogel-btn">JETZT UNTERSTÜTZEN</button>
      <p class="shop-fv-disclaimer">
        Diese Zahlung ist freiwillig und begründet keinen Anspruch auf Gegenleistungen oder Rückerstattung.
      </p>
    </section>
  `;
}

function renderFrueherVogelBadgeState(status: FrueherVogelStatus): string {
  const p = SHOP_CATALOG.frueherVogel;
  const dateStr = status.paidAt ? formatDateDE(status.paidAt) : '—';
  return `
    <section class="shop-section shop-section--active">
      <div class="shop-section-head">
        <span class="shop-section-title">${p.title} – ALPHA-UNTERSTÜTZUNG</span>
      </div>
      <span class="shop-fv-badge">FRÜHER VOGEL</span>
      <p class="shop-desc">
        Du hast Nighttrade in der Alpha unterstützt. Vielen Dank — das bedeutet uns wirklich etwas.
      </p>
      <div class="shop-fv-meta">Unterstützt am: ${dateStr}</div>
    </section>
  `;
}

function renderFrueherVogelThanks(): string {
  const p = SHOP_CATALOG.frueherVogel;
  return `
    <section class="shop-section shop-section--active">
      <div class="shop-section-head">
        <span class="shop-section-title">${p.title} – ALPHA-UNTERSTÜTZUNG</span>
      </div>
      <div class="shop-flash shop-flash--success">DANKE FÜR DEINE UNTERSTÜTZUNG</div>
    </section>
  `;
}

function renderFrueherVogelProcessing(): string {
  const p = SHOP_CATALOG.frueherVogel;
  return `
    <section class="shop-section shop-section--active">
      <div class="shop-section-head">
        <span class="shop-section-title">${p.title} – ALPHA-UNTERSTÜTZUNG</span>
      </div>
      <div class="shop-flash shop-flash--muted">ZAHLUNG WIRD VERARBEITET...</div>
    </section>
  `;
}

interface CheckoutPrefill {
  email: string;
  country: string;
}

/**
 * Produkt-Info für das Checkout-Modal (NT-SCHEINE-PAKETE-01) — trägt alles, was für die
 * § 312j-BGB-Zusammenfassungszeile und die korrekte Button-Beschriftung nötig ist.
 * `scheineAmount: null` bei Früher Vogel (variabler Betrag, Scheine-Reward ist bedingt/
 * kumulativ und wird bewusst nicht als garantierte Zusage in der Zusammenfassung gezeigt).
 */
interface CheckoutProductInfo {
  productKey: string;
  productDisplayName: string;
  amountCents: number;
  scheineAmount: number | null;
  confirmLabel: string;
}

/** Sehr einfache Format-Prüfung — Server bleibt maßgeblich (Zod-Schema). */
function isValidEmailFormat(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function removeCheckoutModal(): void {
  document.getElementById('shop-checkout-backdrop')?.remove();
}

function renderCheckoutModalHtml(product: CheckoutProductInfo, prefill: CheckoutPrefill | null): string {
  const countryOptions = Object.entries(EU_COUNTRY_LABELS)
    .map(([code, label]) => {
      const selected = (prefill?.country ?? 'DE') === code ? ' selected' : '';
      return `<option value="${code}"${selected}>${label}</option>`;
    })
    .join('');

  return `
    <div class="modal-backdrop" id="shop-checkout-backdrop">
      <div class="modal modal--narrow" role="dialog" aria-modal="true" aria-label="Checkout">
        <div class="modal-header"><h2>CHECKOUT</h2></div>
        <div class="modal-body">
          <div class="shop-checkout-error" id="shop-checkout-error"></div>

          <label class="shop-checkout-label" for="shop-checkout-email">E-Mail-Adresse</label>
          <input
            type="email"
            id="shop-checkout-email"
            class="shop-checkout-input"
            maxlength="254"
            value="${prefill?.email ?? ''}"
          >

          <label class="shop-checkout-label" for="shop-checkout-country">Land</label>
          <select id="shop-checkout-country" class="shop-checkout-select">${countryOptions}</select>

          <label class="shop-checkout-consent">
            <input type="checkbox" id="shop-checkout-consent">
            <span>
              Ich stimme ausdrücklich zu, dass mit der Ausführung des Vertrags vor Ablauf der
              Widerrufsfrist begonnen wird. Mir ist bekannt, dass ich dadurch mein Widerrufsrecht
              verliere (§ 356 Abs. 5 BGB).
            </span>
          </label>

          <p class="shop-checkout-privacy-hint">
            Deine E-Mail-Adresse und dein Land werden verschlüsselt gespeichert und ausschließlich
            für die Kaufbestätigung und die gesetzliche Aufbewahrung von Belegen (10 Jahre, § 147 AO)
            verwendet. <a href="/datenschutz">Datenschutzerklärung</a>
          </p>

          <!-- § 312j BGB Buttonlösung: unmittelbar über dem Bestätigungs-Button steht,
               was gekauft wird und was es kostet. -->
          <div class="shop-checkout-summary">
            <div class="shop-checkout-summary-row">
              <span>PRODUKT</span><span>${product.productDisplayName}</span>
            </div>
            ${product.scheineAmount !== null ? `
            <div class="shop-checkout-summary-row">
              <span>SCHEINE</span><span>${String(product.scheineAmount)}</span>
            </div>` : ''}
            <div class="shop-checkout-summary-row shop-checkout-summary-row--total">
              <span>GESAMTPREIS (INKL. USt.)</span><span>${formatEUR(product.amountCents / 100)}</span>
            </div>
          </div>
        </div>
        <div class="modal-actions shop-checkout-actions">
          <button class="shop-btn" id="shop-checkout-cancel">ABBRECHEN</button>
          <button class="shop-btn shop-btn--primary" id="shop-checkout-submit" disabled>${product.confirmLabel}</button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Öffnet das Checkout-Formular (E-Mail/Land/Widerruf) für ein Produkt VOR dem
 * Mollie-Redirect. Bei Erfolg leitet die Funktion selbst zur Checkout-URL weiter.
 */
function openCheckoutModal(product: CheckoutProductInfo): void {
  void api
    .get<CheckoutPrefill | null>('/shop/checkout/prefill')
    .catch(() => null)
    .then((prefill) => {
      document.body.insertAdjacentHTML('beforeend', renderCheckoutModalHtml(product, prefill));

      const backdrop = document.getElementById('shop-checkout-backdrop');
      const emailInput = document.getElementById('shop-checkout-email') as HTMLInputElement | null;
      const countrySelect = document.getElementById('shop-checkout-country') as HTMLSelectElement | null;
      const consentCheckbox = document.getElementById('shop-checkout-consent') as HTMLInputElement | null;
      const submitBtn = document.getElementById('shop-checkout-submit') as HTMLButtonElement | null;
      const cancelBtn = document.getElementById('shop-checkout-cancel');
      const errorEl = document.getElementById('shop-checkout-error');
      if (!backdrop || !emailInput || !countrySelect || !consentCheckbox || !submitBtn || !cancelBtn || !errorEl) {
        return;
      }

      const updateSubmitState = (): void => {
        const emailValid = isValidEmailFormat(emailInput.value);
        emailInput.classList.toggle('shop-checkout-input--invalid', emailInput.value.length > 0 && !emailValid);
        submitBtn.disabled = !emailValid || !consentCheckbox.checked;
      };

      emailInput.addEventListener('input', updateSubmitState);
      consentCheckbox.addEventListener('change', updateSubmitState);
      updateSubmitState();

      cancelBtn.addEventListener('click', removeCheckoutModal);
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) removeCheckoutModal();
      });

      submitBtn.addEventListener('click', () => {
        if (submitBtn.disabled) return;
        submitBtn.disabled = true;
        submitBtn.textContent = 'WIRD VERARBEITET...';
        errorEl.textContent = '';

        api
          .post<{ checkoutUrl: string }>('/shop/checkout', {
            productKey: product.productKey,
            amountCents: product.amountCents,
            email: emailInput.value.trim(),
            country: countrySelect.value,
            widerrufConsent: true,
          })
          .then((res) => {
            window.location.href = res.checkoutUrl;
          })
          .catch((err: unknown) => {
            const message = err instanceof ApiError ? err.message : 'Checkout fehlgeschlagen.';
            errorEl.textContent = message;
            submitBtn.disabled = false;
            submitBtn.textContent = product.confirmLabel;
          });
      });
    });
}

/**
 * Bindet Eingabe-Validierung + Checkout-Klick für das Früher-Vogel-Formular (Zustand A).
 * `alreadyPaidCents` — kumulative Summe bereits geleisteter Zahlungen (Aufgabe 5): der
 * Checkmark-Hinweis rechnet gegen den REST-Stand bis zur Schwelle, nicht mehr gegen die
 * volle Schwelle selbst.
 */
function bindFrueherVogelForm(alreadyPaidCents: number): void {
  const input = document.getElementById('shop-fv-amount') as HTMLInputElement | null;
  const hint = document.getElementById('shop-fv-hint');
  const btn = document.getElementById('shop-frueher-vogel-btn') as HTMLButtonElement | null;
  if (!input || !hint || !btn) return;

  const updateState = (): void => {
    const { valid, cents } = validateFrueherVogelAmount(input.value);
    input.classList.toggle('shop-fv-input--invalid', !valid);

    if (!valid) {
      btn.disabled = true;
      btn.textContent = 'MINDESTBETRAG: 2,00 €';
      hint.textContent = '';
      return;
    }

    btn.disabled = false;
    btn.textContent = 'JETZT UNTERSTÜTZEN';
    hint.textContent =
      alreadyPaidCents + cents >= FRUEHER_VOGEL_BADGE_THRESHOLD_CENTS
        ? '✓ Mit dieser Zahlung erreichst du das FRÜHER VOGEL Badge.'
        : '';
  };

  input.addEventListener('input', updateState);
  updateState();

  btn.addEventListener('click', () => {
    const { valid, cents } = validateFrueherVogelAmount(input.value);
    if (!valid) return;

    // Checkout-Formular (E-Mail/Land/Widerruf) VOR dem Mollie-Redirect (NT-MOLLIE-CHECKOUT-01)
    openCheckoutModal({
      productKey: 'frueher_vogel',
      productDisplayName: SHOP_CATALOG.frueherVogel.title,
      amountCents: cents,
      scheineAmount: null,
      confirmLabel: 'ZAHLUNGSPFLICHTIG UNTERSTÜTZEN',
    });
  });
}

/** Wiederverwendbarer Kopf für gesperrte Sektionen (DEMNÄCHST-Badge). */
function lockedHead(title: string): string {
  return `
    <div class="shop-section-head">
      <span class="shop-section-title">${title}</span>
      <span class="shop-badge-soon">DEMNÄCHST</span>
    </div>
  `;
}

type ScheinePaketResultKind = 'paid' | 'pending' | 'failed';

interface ScheinePaketResultBanner {
  productKey: string;
  status: ScheinePaketResultKind;
}

function renderScheinePaketFlash(status: ScheinePaketResultKind): string {
  if (status === 'paid') return '<div class="shop-flash shop-flash--success">SCHEINE GUTGESCHRIEBEN</div>';
  if (status === 'pending') return '<div class="shop-flash shop-flash--muted">ZAHLUNG WIRD VERARBEITET...</div>';
  return '<div class="shop-flash shop-flash--error">ZAHLUNG FEHLGESCHLAGEN</div>';
}

function renderScheinePakete(resultBanner: ScheinePaketResultBanner | null): string {
  const p = SHOP_CATALOG.scheinePakete;
  const cards = p.items.map((item) => {
    const bonus = item.bonus !== null ? `<span class="shop-package-bonus">+${String(item.bonus)}</span>` : '';
    const banner = resultBanner?.productKey === item.productKey ? renderScheinePaketFlash(resultBanner.status) : '';
    return `
      <div class="shop-package-card">
        <span class="shop-package-name">${item.name}</span>
        <span class="shop-package-scheine">${String(item.scheine)} Scheine ${bonus}</span>
        <span class="shop-package-price">${formatEUR(item.priceEUR)}</span>
        <button class="shop-btn shop-btn--primary" data-scheine-buy="${item.productKey}">JETZT KAUFEN</button>
        ${banner}
      </div>
    `;
  }).join('');
  return `
    <section class="shop-section shop-section--active">
      <div class="shop-section-head">
        <span class="shop-section-title">${p.title}</span>
      </div>
      <div class="shop-package-grid">${cards}</div>
    </section>
  `;
}

/** Bindet die Kauf-Buttons der Scheine-Pakete — ruft denselben Checkout wie Früher Vogel auf. */
function bindScheinePaketeButtons(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-scheine-buy]').forEach((btn) => {
    const productKey = btn.dataset.scheineBuy;
    const item = SHOP_CATALOG.scheinePakete.items.find((i) => i.productKey === productKey);
    if (!item) return;

    btn.addEventListener('click', () => {
      openCheckoutModal({
        productKey: item.productKey,
        productDisplayName: item.name,
        amountCents: Math.round(item.priceEUR * 100),
        scheineAmount: item.scheine,
        confirmLabel: 'ZAHLUNGSPFLICHTIG KAUFEN',
      });
    });
  });
}

function renderBattlePass(): string {
  const p = SHOP_CATALOG.battlePass;
  return `
    <section class="shop-section shop-section--locked">
      ${lockedHead(p.title)}
      <p class="shop-desc">${String(p.priceScheine)} Scheine pro Saison — ${p.description}</p>
      <button class="shop-btn" disabled>BALD</button>
    </section>
  `;
}

function renderCommunityPass(): string {
  const p = SHOP_CATALOG.communityPass;
  return `
    <section class="shop-section shop-section--locked">
      ${lockedHead(p.title)}
      <p class="shop-desc">${formatEUR(p.priceEURMonthly)}/Monat — ${p.description}</p>
      <button class="shop-btn" disabled>BALD</button>
    </section>
  `;
}

function renderCommunityPassCombo(): string {
  const p = SHOP_CATALOG.communityPassCombo;
  return `
    <section class="shop-section shop-section--locked">
      ${lockedHead(p.title)}
      <p class="shop-desc">${formatEUR(p.priceEURMonthly)}/Monat — ${p.description}</p>
      <button class="shop-btn" disabled>BALD</button>
    </section>
  `;
}

function renderSupporterAbo(): string {
  const p = SHOP_CATALOG.supporterAbo;
  const tiers = p.tiers.map((t) => `${t.name} · ${formatEUR(t.priceEURMonthly)}/Monat (${String(t.scheineMonthly)} Scheine)`).join('<br>');
  return `
    <section class="shop-section shop-section--locked">
      ${lockedHead(p.title)}
      <div class="shop-tier-row">${tiers}</div>
      <button class="shop-btn" disabled>BALD</button>
    </section>
  `;
}

// URL-Parameter nach Auswertung aus History entfernen (kein doppeltes Flash nach Reload)
function cleanShopUrlParams(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('payment')) {
    url.searchParams.delete('payment');
    window.history.replaceState({}, '', url.toString());
  }
}

export async function renderShop(): Promise<void> {
  addStyles();
  const app = document.getElementById('app');
  if (!app) return;

  const paymentParam = new URLSearchParams(window.location.search).get('payment');
  const isPaymentResult = paymentParam === 'frueher_vogel_result';
  // NT-SCHEINE-PAKETE-01: generisches ?payment=<productKey>_result-Muster für die fünf
  // Pakete — kein hartcodierter String pro Paket, sondern Abgleich gegen den Katalog.
  const scheinePaketResultKey =
    SHOP_CATALOG.scheinePakete.items.find((item) => paymentParam === `${item.productKey}_result`)?.productKey ?? null;

  app.innerHTML = `
    <div class="shop-page">
      <button class="shop-back" id="shop-back-btn">Zurück</button>
      <h1 class="shop-title">NIGHTTRADE <span class="shop-title-accent">//</span> SHOP</h1>
      <p class="shop-intro">
        Während der Alpha sind der Frühe Vogel und die Scheine-Pakete aktiv buchbar.
        Alle weiteren Angebote sind eine Vorschau und noch nicht buchbar.
      </p>
      <div id="shop-fv-section">Lade...</div>
      <div id="shop-scheine-section">${renderScheinePakete(null)}</div>
      ${renderBattlePass()}
      ${renderCommunityPass()}
      ${renderCommunityPassCombo()}
      ${renderSupporterAbo()}
    </div>
  `;

  document.getElementById('shop-back-btn')?.addEventListener('click', () => {
    navigate('/dashboard');
  });

  cleanShopUrlParams();
  bindScheinePaketeButtons();

  const fvSection = document.getElementById('shop-fv-section');
  if (fvSection) {
    try {
      const status = await api.get<FrueherVogelStatus>('/payment/frueher-vogel/status');
      if (status.hasBadge) {
        fvSection.innerHTML = renderFrueherVogelBadgeState(status);
      } else if (isPaymentResult) {
        fvSection.innerHTML = renderFrueherVogelThanks();
        void refreshBalance();
      } else {
        const alreadyPaidCents = status.amountCents ?? 0;
        fvSection.innerHTML = renderFrueherVogelForm(alreadyPaidCents);
        bindFrueherVogelForm(alreadyPaidCents);
      }
    } catch {
      if (isPaymentResult) {
        fvSection.innerHTML = renderFrueherVogelProcessing();
      } else {
        fvSection.innerHTML = renderFrueherVogelForm(0);
        bindFrueherVogelForm(0);
      }
    }
  }

  if (scheinePaketResultKey !== null) {
    try {
      const result = await api.get<ShopCheckoutResultResponse>(
        `/shop/checkout/result?productKey=${scheinePaketResultKey}`,
      );
      if (result.status !== 'none') {
        const scheineSection = document.getElementById('shop-scheine-section');
        if (scheineSection) {
          scheineSection.innerHTML = renderScheinePakete({ productKey: scheinePaketResultKey, status: result.status });
          bindScheinePaketeButtons();
        }
        if (result.status === 'paid') {
          void refreshBalance();
        }
      }
    } catch {
      // Kein Status abrufbar (z.B. Netzwerkfehler) — Paket-Sektion bleibt ohne Banner nutzbar.
    }
  }
}
